import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js'

import {
  characterModelUrl,
  KAYKIT_ANIM_GENERAL_URL,
  KAYKIT_ANIM_MOVEMENT_URL,
  KAYKIT_MODEL_SCALE,
  resolveCharacterId,
} from '../model/characterCatalog'
import type { AvatarAppearance } from '../model/types'

import { applyKayKitEquipment } from './applyKayKitEquipment'
import type { BuiltAvatar } from './buildAvatarMesh'
import type { QuaterniusClip } from './buildQuaterniusAvatar'

const loader = new GLTFLoader()
const modelCache = new Map<string, THREE.Group>()
let sharedClips: THREE.AnimationClip[] | null = null

async function loadKayKitClips(): Promise<THREE.AnimationClip[]> {
  if (sharedClips) return sharedClips
  const [move, general] = await Promise.all([
    loader.loadAsync(KAYKIT_ANIM_MOVEMENT_URL),
    loader.loadAsync(KAYKIT_ANIM_GENERAL_URL),
  ])
  sharedClips = [...(move.animations ?? []), ...(general.animations ?? [])]
  return sharedClips
}

async function loadKayKitModel(url: string): Promise<THREE.Group> {
  const hit = modelCache.get(url)
  if (hit) return hit
  const gltf = await loader.loadAsync(url)
  const scene = gltf.scene as THREE.Group
  modelCache.set(url, scene)
  return scene
}

function createNameSprite(text: string): THREE.Sprite {
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 64
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d context unavailable')
  const draw = (label: string) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = 'rgba(0,0,0,0.6)'
    ctx.beginPath()
    ctx.roundRect(8, 12, 240, 40, 12)
    ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.font = 'bold 22px system-ui,Segoe UI,sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(label.slice(0, 18), 128, 32, 220)
  }
  draw(text)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }),
  )
  sprite.scale.set(0.75, 0.18, 1)
  sprite.position.set(0, 0.42, 0.05)
  sprite.visible = false
  ;(sprite.userData as { redraw: (t: string) => void }).redraw = (t: string) => {
    draw(t)
    tex.needsUpdate = true
  }
  return sprite
}

const CLIP_ALIASES: Record<QuaterniusClip, string[]> = {
  Walk: ['Walking_A', 'Walking_B', 'Walking_C'],
  Idle: ['Idle_A', 'Idle_B'],
  Run: ['Running_A', 'Running_B'],
  Punch: ['Throw', 'Hit_A', 'Hit_B'],
  Jump: ['Jump_Full_Short', 'Jump_Full_Long', 'Jump_Start'],
}

function resolveClip(
  actions: Map<string, THREE.AnimationAction>,
  name: QuaterniusClip,
): THREE.AnimationAction | undefined {
  for (const alias of CLIP_ALIASES[name]) {
    const hit = actions.get(alias)
    if (hit) return hit
  }
  return [...actions.values()][0]
}

/**
 * KayKit Adventurers — model GLB + shared Rig_Medium animation packs.
 */
export async function buildKayKitAvatar(
  appearance: AvatarAppearance,
  displayName: string,
  initialClip: QuaterniusClip = 'Walk',
): Promise<BuiltAvatar> {
  const id = resolveCharacterId(appearance.characterId)
  const url = characterModelUrl(id)
  const [template, clips] = await Promise.all([loadKayKitModel(url), loadKayKitClips()])

  const root = new THREE.Group()
  const body = new THREE.Group()
  root.add(body)

  const cloned = cloneSkinned(template) as THREE.Group
  cloned.rotation.set(0, 0, 0)
  cloned.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.castShadow = false
      obj.receiveShadow = false
      obj.frustumCulled = true
    }
  })

  const scale = KAYKIT_MODEL_SCALE * appearance.bodyScale
  body.rotation.set(0, 0, 0)
  body.scale.setScalar(scale)
  body.add(cloned)

  body.updateWorldMatrix(true, true)
  const bounds = new THREE.Box3().setFromObject(body)
  if (Number.isFinite(bounds.min.y)) {
    const lift = -bounds.min.y
    if (Math.abs(lift) < 0.45) body.position.y += lift
  }
  body.userData.groundY = body.position.y

  const leftArm = new THREE.Group()
  const rightArm = new THREE.Group()
  const leftLeg = new THREE.Mesh(
    new THREE.BoxGeometry(0.01, 0.01, 0.01),
    new THREE.MeshBasicMaterial(),
  )
  const rightLeg = new THREE.Mesh(
    new THREE.BoxGeometry(0.01, 0.01, 0.01),
    new THREE.MeshBasicMaterial(),
  )
  leftLeg.visible = false
  rightLeg.visible = false
  body.add(leftArm, rightArm, leftLeg, rightLeg)

  const head = new THREE.Group()
  head.position.y = 1.9
  body.add(head)

  const mixer = new THREE.AnimationMixer(cloned)
  const actions = new Map<string, THREE.AnimationAction>()
  for (const clip of clips) {
    actions.set(clip.name, mixer.clipAction(clip))
  }

  const playLoop = (name: QuaterniusClip) => {
    const next = resolveClip(actions, name)
    if (!next) return
    mixer.stopAllAction()
    next.reset()
    next.setLoop(THREE.LoopRepeat, Infinity)
    next.clampWhenFinished = false
    next.timeScale = name === 'Walk' || name === 'Run' ? 0.85 : 1
    next.play()
  }

  const playOnce = (name: QuaterniusClip) => {
    const next = resolveClip(actions, name)
    if (!next) return
    mixer.stopAllAction()
    next.reset()
    next.setLoop(THREE.LoopOnce, 1)
    next.clampWhenFinished = true
    next.play()
  }

  playLoop(initialClip)

  await applyKayKitEquipment(cloned, id, appearance.equipment)

  root.userData.mixer = mixer
  root.userData.playClip = playOnce
  root.userData.playIdle = () => playLoop('Idle')
  root.userData.playWalk = () => playLoop('Walk')

  const nameSprite = createNameSprite(displayName)
  nameSprite.position.set(0, 2.7, 0.08)
  nameSprite.scale.set(1.1 / Math.max(0.01, scale), 0.28 / Math.max(0.01, scale), 1)
  body.add(nameSprite)

  const dispose = () => {
    mixer.stopAllAction()
    root.traverse((obj) => {
      if (!(obj instanceof THREE.Sprite)) return
      const m = obj.material
      if (m.map) m.map.dispose()
      m.dispose()
    })
  }

  return {
    root,
    body,
    leftArm,
    rightArm,
    leftLeg,
    rightLeg,
    head,
    nameSprite,
    quality: 'high',
    setNameVisible: (v) => {
      nameSprite.visible = v
    },
    updateName: (name) => {
      ;(nameSprite.userData as { redraw: (t: string) => void }).redraw(name)
    },
    dispose,
  }
}
